
import authenticatedAxios from "./authenticatedAxios";
import axios from "axios"; // Keep for FundCheck API which might be public
import RedisInfo from "./RedisInfo";

// ============================================
// VERSION: 2025-10-29-FINAL-FIX-v2
// ============================================
console.log("🟢🟢🟢 UsageReportData.js LOADED - VERSION 2025-10-29-FINAL-FIX-v2 🟢🟢🟢");

// CRITICAL FIX: Add fallback for API URL if not set during build
const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;

/** PK format: PARENT_GROUP#DEALER_MAP_CODE#LOC_CODE (matches backend MassDealersResponse). */
const dealerShipIdFromRow = (item) => {
  if (!item) return null;
  const pg = item.parentGroup;
  const dm = item.dealerMapCode;
  const loc = item.locCode;
  if (pg && dm && loc) {
    return `${pg}#${dm}#${loc}`;
  }
  return null;
};

const parseBalanceString = (s) => {
  if (s == null || s === "") return null;
  const n = parseFloat(String(s).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

const normalizePagedResponse = (payload) => {
  if (payload && Array.isArray(payload.content)) {
    return {
      content: payload.content,
      totalElements:
        typeof payload.totalElements === "number"
          ? payload.totalElements
          : payload.content.length,
      totalPages:
        typeof payload.totalPages === "number"
          ? payload.totalPages
          : 1,
      page: typeof payload.page === "number" ? payload.page : 0,
      size:
        typeof payload.size === "number"
          ? payload.size
          : payload.content.length,
    };
  }
  const arr = Array.isArray(payload) ? payload : [];
  return {
    content: arr,
    totalElements: arr.length,
    totalPages: 1,
    page: 0,
    size: arr.length,
  };
};

/**
 * Fetch ACTIVE service usage report.
 */
const serviceUsageReport = async (page, size) => {
  try {
    const params = {};
    if (Number.isInteger(page) && Number.isInteger(size)) {
      params.page = page;
      params.size = size;
    }
    const response = await authenticatedAxios.get(
      `/api/v1/report/massReportForActiveDealers`,
      { params }
    );
    const paged = normalizePagedResponse(response.data);
    return {
      ...paged,
      content: paged.content.map((item) => ({
      massName: item.massName,
      mobile: item.mobile,
      region: item.region,
      serviceName: item.serviceName,
      planName: item.planName,
      massCode: item.massCode,
      status: item.status,
      orderDate: item.orderDate,
      releaseDate: item.releaseDate,
      endDate: item.endDate,
      price: item.price,
      autoDebit: item.autoDebit,
      parentGroup: item.parentGroup,
      dealerMapCode: item.dealerMapCode,
      locCode: item.locCode,
      activeServiceCount: item.activeServiceCount,
      consumption: item.consumption,
      totalConsumedPercentage: item.totalConsumedPercentage,
      minutesPurchased: item.minutesPurchased,
      messagesPurchased: item.messagesPurchased,
      consumedMinutes: item.consumedMinutes,
      consumedMessages: item.consumedMessages,
      // Keep fallback aliases so UI works even if backend key naming varies.
      totalConsumedMinutes:
        item.totalConsumedMinutes ??
        item.totalConsumedMinute ??
        item.totalMinutesConsumed,
      totalConsumedMessages:
        item.totalConsumedMessages ??
        item.totalConsumedMessage ??
        item.totalMessagesConsumed,
      lastConsumptionDate: item.lastConsumptionDate,
      invoiceNumber: item.invoiceNumber,
      // "Expired" when the order is 100% consumed (backend also sets status to "Expired").
      expirationStatus: item.expirationStatus,
      })),
    };
  } catch (error) {
    console.error("Error fetching service usage report:", error);
    throw error;
  }
};

/**
 * Fetch INACTIVE MASS report
 */
const inactiveMasReport = async (page, size) => {
  try {
    const params = {};
    if (Number.isInteger(page) && Number.isInteger(size)) {
      params.page = page;
      params.size = size;
    }
    const response = await authenticatedAxios.get(
      `/api/v1/report/massReportForDealersWithInActiveService`,
      { params }
    );
    return normalizePagedResponse(response.data);
  } catch (error) {
    console.error("Error fetching inactive MASS report:", error);
    throw error;
  }
};

/**
 * Fetch onboarding balance map for MSIL admin.
 * Returns Map<dealerShipId, { accountBalance, accountBalanceTimestamp, dealerShipName, ... }>.
 * Full row data is stored so detailedAccountStatus can create placeholder rows for
 * never-purchased dealers (those not in the service usage report).
 */
const fetchOnboardingBalances = async () => {
  const ob = await authenticatedAxios.get(`/api/v1/report/onboardingReportRows`);
  const rows = ob.data;
  const balanceByDealer = new Map();
  if (Array.isArray(rows)) {
    rows.forEach((row) => {
      if (row?.dealerShipId) {
        balanceByDealer.set(row.dealerShipId, {
          accountBalance: parseBalanceString(row.accountBalance),
          accountBalanceTimestamp: row.lastStatusUpdatedAt || null,
          dealerShipName: row.dealerShipName || null,
          regionCode: row.regionCode || null,
          massRegion: row.massRegion || null,
          mulSrvDealerCode: row.mulSrvDealerCode || null,
          forCode: row.forCode || null,
          outletCode: row.outletCode || null,
          parentGroup: row.parentGroup || null,
          dealerMapCode: row.dealerMapCode || null,
          locationCode: row.locationCode || null,
        });
      }
    });
  }
  return balanceByDealer;
};

/**
 * Merge a balance map (from fetchOnboardingBalances) into an array of service rows.
 * Used by the component to merge after the async balance fetch completes.
 */
const mergeServicesWithBalances = (services, balanceByDealer) => {
  return services.map((item) => {
    const dealerShipId = dealerShipIdFromRow(item);
    const ob = dealerShipId ? balanceByDealer.get(dealerShipId) : null;
    return {
      ...item,
      accountBalance: ob?.accountBalance ?? null,
      accountBalanceTimestamp: ob?.accountBalanceTimestamp
        ? new Date(ob.accountBalanceTimestamp)
        : null,
    };
  });
};

/**
 * Attach activeServicesCount to each service row (normalises the count field).
 */
const attachActiveServicesCount = (services) =>
  services.map((item) => {
    let activeServicesCount;
    if (
      item.activeServiceCount !== undefined &&
      item.activeServiceCount !== null &&
      item.activeServiceCount !== ""
    ) {
      const n = Number(item.activeServiceCount);
      activeServicesCount = Number.isFinite(n) ? n : undefined;
    }
    return { ...item, activeServicesCount };
  });

/**
 * Deduplicate service rows for MASS Account Status: one row per MASS Code.
 * Prefers Active status over others; within the same status prefers the most recent order date.
 * Rows missing a massCode (or "N/A") are kept as-is since they cannot be grouped.
 * The activeServicesCount field is already set per-dealer by the backend, so it is preserved.
 */
const deduplicateByMassCode = (rows) => {
  if (!rows || rows.length === 0) return rows;
  const byMassCode = new Map();
  const noMassCodeRows = [];

  rows.forEach((row) => {
    const code = row.massCode;
    if (!code || code === "N/A") {
      noMassCodeRows.push(row);
      return;
    }
    const existing = byMassCode.get(code);
    if (!existing) {
      byMassCode.set(code, row);
      return;
    }
    const rowIsActive = row.status === "Active";
    const existingIsActive = existing.status === "Active";
    if (rowIsActive && !existingIsActive) {
      byMassCode.set(code, row);
    } else if (rowIsActive === existingIsActive) {
      const rD = row.orderDate ? new Date(row.orderDate).getTime() : 0;
      const eD = existing.orderDate ? new Date(existing.orderDate).getTime() : 0;
      if (rD > eD) byMassCode.set(code, row);
    }
  });

  return [...Array.from(byMassCode.values()), ...noMassCodeRows];
};

/**
 * Fetch detailed account status.
 *
 * Fetches ALL service rows (no pagination at source), deduplicates to one row per
 * MASS Code so the table shows each dealer once with their actual active service count,
 * then paginates the deduplicated list before returning.
 *
 * For MSIL admin this is a two-step operation (service data + onboarding balances).
 * Pass `preloadedBalanceMap` to skip the onboarding fetch when the component already
 * has a cached copy:
 *   - undefined  → fetch onboarding now (default; used by export and non-cached loads)
 *   - Map        → use the pre-fetched map (page changes with cached data)
 *   - null       → skip balances (component fetches independently); onboarding rows are
 *                  still fetched here to create placeholder rows for never-purchased dealers
 *
 * For non-admin users the FundCheck call is made here as before.
 */
const detailedAccountStatus = async (page, size, preloadedBalanceMap = undefined) => {
  try {
    // Fetch ALL rows without pagination so we can deduplicate before paginating.
    const servicesPaged = await serviceUsageReport();
    const services = servicesPaged.content || [];
    const role = sessionStorage.getItem("role");
    const isMSILAdmin =
      role === "MSIL" || role === "SUPER_ADMIN" || role === "ADMIN";

    let withBalances;

    if (isMSILAdmin) {
      let balanceByDealer;
      let fullOnboardingMap; // enriched Map with full row data for placeholder creation

      if (preloadedBalanceMap instanceof Map) {
        // Use caller-supplied cache — no extra network call.
        balanceByDealer = preloadedBalanceMap;
        fullOnboardingMap = preloadedBalanceMap;
      } else if (preloadedBalanceMap === null) {
        // Caller will supply balances later; fetch onboarding rows to build placeholder rows now.
        balanceByDealer = null;
        try {
          fullOnboardingMap = await fetchOnboardingBalances();
        } catch (e) {
          console.warn("onboardingReportRows failed; missing dealers may not show:", e);
          fullOnboardingMap = null;
        }
      } else {
        // Default: fetch onboarding balances now (export path / uncached first load).
        try {
          balanceByDealer = await fetchOnboardingBalances();
          fullOnboardingMap = balanceByDealer;
        } catch (e) {
          console.warn("onboardingReportRows failed; balances may be empty:", e);
          balanceByDealer = new Map();
          fullOnboardingMap = new Map();
        }
      }

      const merged = balanceByDealer
        ? mergeServicesWithBalances(services, balanceByDealer)
        : services.map((item) => ({ ...item, accountBalance: null, accountBalanceTimestamp: null }));

      withBalances = attachActiveServicesCount(merged);

      // Add placeholder rows for onboarded dealers who have never purchased any services.
      // These rows show account balance but "NA" (null → "-") for all order fields.
      if (fullOnboardingMap && fullOnboardingMap.size > 0) {
        const dealerIdsWithServices = new Set(
          withBalances.map((item) => dealerShipIdFromRow(item)).filter(Boolean)
        );
        fullOnboardingMap.forEach((obRow, dealerShipId) => {
          if (!dealerIdsWithServices.has(dealerShipId)) {
            const delim = "#";
            const massCode =
              obRow.mulSrvDealerCode && obRow.forCode && obRow.outletCode
                ? `${obRow.mulSrvDealerCode}${delim}${obRow.forCode}${delim}${obRow.outletCode}`
                : null;
            withBalances.push({
              massName: obRow.dealerShipName || null,
              region: obRow.massRegion || obRow.regionCode || null,
              massCode,
              parentGroup: obRow.parentGroup || null,
              dealerMapCode: obRow.dealerMapCode || null,
              locCode: obRow.locationCode || null,
              serviceName: null,
              planName: null,
              status: null,
              orderDate: null,
              releaseDate: null,
              endDate: null,
              price: null,
              autoDebit: null,
              invoiceNumber: null,
              accountBalance: obRow.accountBalance ?? null,
              accountBalanceTimestamp: obRow.accountBalanceTimestamp
                ? new Date(obRow.accountBalanceTimestamp)
                : null,
              activeServicesCount: 0,
              activeServiceCount: "0",
            });
          }
        });
      }
    } else {
      // ── Non-admin: FundCheck for the logged-in dealer ────────────────────────
      let singleBalance = 0;
      let singleTimestamp = null;

      try {
        const redisData = await RedisInfo.redisDetails();
        const forCode = "00";
        const outletCode = "00";
        const dealerCode =
          redisData.DealerCode ||
          sessionStorage.getItem("multiServiceDealerCode") ||
          localStorage.getItem("multiServiceDealerCode");

        const fundCheckResponse = await axios.post(
          `${apiUrl}/api/v1/FundCheck`,
          {
            CustCategory: "MASS",
            DealerCode: "MASS" + dealerCode,
            ForCode: forCode,
            OutletCode: outletCode,
          },
          {
            headers: { "Content-Type": "application/json" },
            validateStatus: (status) => status >= 200 && status < 500,
          }
        );

        if (
          fundCheckResponse.data?.ErrorCode !== -1 &&
          fundCheckResponse.data?.Status !== "Error" &&
          fundCheckResponse.data?.Data?.length > 0
        ) {
          const balanceData = fundCheckResponse.data.Data[0];
          if (balanceData.AMOUNT != null) {
            singleBalance = Number(balanceData.AMOUNT);
            singleTimestamp = new Date();
          }
        }
      } catch (fundCheckError) {
        console.error("FundCheck balance error:", fundCheckError);
      }

      withBalances = attachActiveServicesCount(
        services.map((item) => ({
          ...item,
          accountBalance: singleBalance,
          accountBalanceTimestamp: singleTimestamp,
        }))
      );
    }

    // Deduplicate: one row per MASS Code, preserving the backend's activeServicesCount.
    const deduped = deduplicateByMassCode(withBalances);

    // Paginate the deduplicated list manually.
    const total = deduped.length;
    let pageContent;
    if (Number.isInteger(page) && Number.isInteger(size) && size > 0) {
      const from = page * size;
      pageContent = deduped.slice(from, Math.min(from + size, total));
    } else {
      pageContent = deduped;
    }

    return {
      content: pageContent,
      totalElements: total,
      totalPages: size > 0 ? Math.ceil(total / size) : 1,
      page: page ?? 0,
      size: size ?? total,
    };
  } catch (error) {
    console.error("❌ Error in detailedAccountStatus:", error);
    throw error;
  }
};

/**
 * Fetch Financial Report
 */
const financialReport = async (page, size) => {
  try {
    const params = {};
    if (Number.isInteger(page) && Number.isInteger(size)) {
      params.page = page;
      params.size = size;
    }
    const response = await authenticatedAxios.get(
      `/api/v1/report/financialReport`,
      { params }
    );
    return normalizePagedResponse(response.data);
  } catch (error) {
    console.error("Error fetching financial report:", error);
    throw error;
  }
};

/**
 * Fetch usage report details for a specific service
 */
const usageReportDetails = async (serviceName) => {
  try {
    console.log("Fetching usage report details for service:", serviceName);

    const responseRedisData = await RedisInfo.redisDetails();
    const parentGroup = responseRedisData.ParentGroup;
    const dealerMapCode = responseRedisData.DealerMapCode;
    const locCode = responseRedisData.LocCode;

    const serviceParam =
      serviceName?.replace(/^SERVICE#/, "") || serviceName;

    const API_URL = `/api/v1/planUsageDetails/getPlanUsageAndBalanceData/${parentGroup}/${dealerMapCode}/${locCode}/${serviceParam}`;

    const response = await authenticatedAxios.get(API_URL);

    return response.data;
  } catch (error) {
    console.error("Error fetching usage report details:", error);
    throw error;
  }
};

const UsageReportData = {
  serviceUsageReport,
  inactiveMasReport,
  detailedAccountStatus,
  financialReport,
  usageReportDetails,
  fetchOnboardingBalances,
  mergeServicesWithBalances,
};

export default UsageReportData;
